/**
 * The single source of truth for how `ChatWidgetProps` (legacy flat props,
 * the `agent` object and every `settings.*` key) map onto the canonical
 * `WidgetConfig` shape.
 *
 * Resolution rule (applied identically to every key by `normalizeWidgetConfig`):
 *
 *   inline prop (first present of `props`) >
 *   `props.settings` (first present of `settings`) >
 *   remote settings (first present of `settings`) >
 *   `fallback`
 *
 * "Present" means not `undefined`, `null` or `''`. Keys flagged `merge` are
 * object configs: their sources are shallow-merged (remote < inline settings <
 * prop) instead of first-wins.
 */

export interface ConfigKeySpec {
  /** Canonical dot path inside `WidgetConfig`. */
  key: string;
  /** Inline prop paths (dot paths into `ChatWidgetProps`), highest priority first. */
  props?: string[];
  /** Paths inside the `settings` object, highest priority first. `settings[0]` is canonical. */
  settings?: string[];
  /** Value used when no source provides one. */
  fallback?: unknown;
  /** Shallow-merge object sources instead of first-wins. */
  merge?: boolean;
  /** The inline props are pure duplicates of `settings[0]` and are deprecated. */
  dup?: boolean;
}

/** Flattened view: one row per accepted input path. */
export interface ConfigAlias {
  source: 'prop' | 'settings';
  /** Dot path within the source (`companyName`, `agent.orgId`, `branding.companyName`). */
  path: string;
  /** Canonical `WidgetConfig` path, or `null` when the input is accepted but ignored. */
  canonical: string | null;
  /** Pure duplicate of another path (or ignored); warned once in development. */
  deprecated: boolean;
  /** Preferred input, shown in deprecation warnings (e.g. `settings.branding.companyName`). */
  replacement?: string;
}

/** Standard duplicated key: `<name>` prop, `settings.<group>.<name>` and legacy flat `settings.<name>`. */
function dup(
  key: string,
  name: string,
  settingsGroup: string,
  fallback?: unknown,
  extra?: Partial<ConfigKeySpec>
): ConfigKeySpec {
  return {
    key,
    props: [name],
    settings: [`${settingsGroup}.${name}`, name],
    fallback,
    dup: true,
    ...extra,
  };
}

/** Prop-only key (callbacks, controlled state, host-only options). */
function propOnly(key: string, name: string, fallback?: unknown): ConfigKeySpec {
  return { key, props: [name], fallback };
}

const EMPTY_OBJECT = Object.freeze({}) as Record<string, never>;
const EMPTY_ARRAY = Object.freeze([]) as unknown as never[];

const CALLBACK_PROPS = [
  'onOpenChange',
  'onSend',
  'onReceive',
  'onError',
  'onThreadCreate',
  'onThreadUpdate',
  'onThreadDelete',
  'onMessageCreate',
  'onHandoffRequest',
  'onOutcome',
  'onAnalyticsEvent',
  'onVoiceStart',
  'onVideoStart',
  'onProductAction',
  'buildMessagePayload',
  'parseResponse',
  'buildThreadUrl',
  'buildThreadRequest',
  'buildThreadUpdateUrl',
  'buildThreadDeleteUrl',
  'parseThreadResponse',
  'parseThreadCreateResponse',
];

export const CONFIG_KEYS: ConfigKeySpec[] = [
  // merchant
  // Not a pure duplicate: the prop also accepts an inline preset object.
  { key: 'merchant.preset', props: ['merchant'], settings: ['merchant'] },

  // branding
  dup('branding.companyName', 'companyName', 'branding', 'Acme Inc.'),
  dup('branding.companyLogo', 'companyLogo', 'branding', ''),
  dup('branding.agentName', 'agentName', 'branding', 'AI Assistant'),
  dup('branding.agentAvatar', 'agentAvatar', 'branding', ''),
  { key: 'branding.theme', props: ['theme'], settings: ['theme', 'branding.theme'], dup: true },
  propOnly('branding.darkMode', 'darkMode', 'light'),
  dup('branding.welcomeMessage', 'welcomeMessage', 'branding', ''),
  dup('branding.suggestedActions', 'suggestedActions', 'branding'),
  dup('branding.bubbleOptions', 'bubbleOptions', 'branding', EMPTY_ARRAY),
  // Empty copy defaults delegate to the resolved locale in the rendering components.
  dup('branding.placeholder', 'placeholder', 'branding', ''),
  {
    key: 'branding.settingsConsoleUrl',
    props: ['settingsConsoleUrl'],
    settings: ['settingsConsoleUrl'],
    fallback: '',
    dup: true,
  },
  {
    key: 'branding.settingsConsoleLabel',
    props: ['settingsConsoleLabel'],
    settings: ['settingsConsoleLabel'],
    fallback: '',
    dup: true,
  },

  // i18n
  dup('i18n.language', 'language', 'branding', 'en'),
  dup('i18n.textDirection', 'textDirection', 'branding', 'auto'),
  dup('i18n.translations', 'translations', 'branding'),

  // agent
  {
    key: 'agent.orgId',
    props: ['orgId', 'agent.orgId'],
    settings: ['agent.orgId', 'orgId'],
    fallback: '',
    dup: true,
  },
  {
    key: 'agent.agentId',
    props: ['agentId', 'agent.agentId'],
    settings: ['agent.agentId', 'agentId'],
    fallback: '',
    dup: true,
  },
  {
    key: 'agent.kbCollection',
    props: ['kbCollection', 'agent.kbCollection'],
    settings: ['agent.kbCollection', 'kbCollection'],
    fallback: '',
    dup: true,
  },
  {
    key: 'agent.kbApiKey',
    props: ['kbApiKey', 'agent.kbApiKey'],
    settings: ['agent.kbApiKey', 'kbApiKey'],
    fallback: '',
    dup: true,
  },
  {
    key: 'agent.kbTopK',
    props: ['kbTopK', 'agent.kbTopK'],
    settings: ['agent.kbTopK', 'kbTopK'],
    dup: true,
  },
  {
    key: 'agent.kbScoreThreshold',
    props: ['kbScoreThreshold', 'agent.kbScoreThreshold'],
    settings: ['agent.kbScoreThreshold', 'kbScoreThreshold'],
    dup: true,
  },
  {
    key: 'agent.conversionPrompt',
    props: ['conversionPrompt'],
    settings: ['conversionPrompt', 'agent.conversionPrompt'],
    fallback: '',
    dup: true,
  },
  { key: 'agent.provider', props: ['provider'], settings: ['provider'] },
  propOnly('agent.aiEnabled', 'aiEnabled'),

  // endpoints
  propOnly('endpoints.settingsEndpoint', 'settingsEndpoint', null),
  dup('endpoints.apiBaseUrl', 'apiBaseUrl', 'endpoints', null),
  dup('endpoints.messageEndpoint', 'messageEndpoint', 'endpoints', null),
  dup('endpoints.messageCreateEndpoint', 'messageCreateEndpoint', 'endpoints', null),
  dup('endpoints.streamEndpoint', 'streamEndpoint', 'endpoints', null),
  dup('endpoints.threadEndpoint', 'threadEndpoint', 'endpoints', null),
  dup('endpoints.threadCreateEndpoint', 'threadCreateEndpoint', 'endpoints', null),
  dup('endpoints.threadUpdateEndpoint', 'threadUpdateEndpoint', 'endpoints', null),
  dup('endpoints.threadDeleteEndpoint', 'threadDeleteEndpoint', 'endpoints', null),
  dup('endpoints.cartEndpoint', 'cartEndpoint', 'endpoints', null),
  dup('endpoints.cartAttributionEndpoint', 'cartAttributionEndpoint', 'endpoints', null),
  dup('endpoints.feedbackEndpoint', 'feedbackEndpoint', 'endpoints', null),
  dup('endpoints.productSearchEndpoint', 'productSearchEndpoint', 'endpoints', null),
  { key: 'endpoints.retryPolicy', props: ['retryPolicy'], settings: ['retryPolicy'], dup: true },

  // behavior
  dup('behavior.position', 'position', 'behavior', 'bottom-right'),
  dup('behavior.zIndex', 'zIndex', 'behavior', 1000),
  dup('behavior.showViewTabs', 'showViewTabs', 'behavior', true),
  {
    key: 'behavior.recordNavigation',
    settings: ['behavior.recordNavigation'],
    fallback: 'available',
  },
  dup('behavior.showTimestamps', 'showTimestamps', 'behavior', false),
  dup('behavior.maxRenderedMessages', 'maxRenderedMessages', 'behavior', 80),
  {
    key: 'behavior.defaultOpen',
    props: ['defaultOpen'],
    settings: ['defaultOpen'],
    fallback: false,
  },
  propOnly('behavior.open', 'open'),
  dup('behavior.autoOpenDelayMs', 'autoOpenDelayMs', 'behavior'),
  {
    key: 'behavior.autoOpenRules',
    props: ['autoOpenRules'],
    settings: ['autoOpenRules'],
    fallback: EMPTY_OBJECT,
    merge: true,
    dup: true,
  },
  dup('behavior.lazyLoad', 'lazyLoad', 'behavior', false),
  { key: 'behavior.subject', props: ['subject'], settings: ['subject'] },
  { key: 'behavior.channel', props: ['channel'], settings: ['channel'], fallback: 'chat' },
  { key: 'behavior.threadId', props: ['threadId'], settings: ['threadId'] },
  {
    key: 'behavior.threadPollIntervalMs',
    props: ['threadPollIntervalMs'],
    settings: ['behavior.threadPollIntervalMs', 'threadPollIntervalMs'],
    fallback: 0,
  },
  {
    key: 'behavior.department',
    props: ['department'],
    settings: ['department'],
    fallback: 'Customer Support',
  },
  {
    key: 'behavior.customFields',
    props: ['customFields'],
    settings: ['customFields'],
    fallback: EMPTY_OBJECT,
  },
  { key: 'behavior.records', props: ['records'], settings: ['records'], fallback: EMPTY_OBJECT },
  {
    key: 'behavior.availability',
    props: ['availability'],
    settings: ['availability'],
    fallback: EMPTY_OBJECT,
    merge: true,
    dup: true,
  },
  {
    key: 'behavior.offlineMessage',
    props: ['offlineMessage'],
    settings: ['offlineMessage'],
    fallback: '',
    dup: true,
  },
  {
    key: 'behavior.preChatForm',
    props: ['preChatForm'],
    settings: ['preChatForm'],
    fallback: EMPTY_OBJECT,
    merge: true,
    dup: true,
  },
  propOnly('behavior.customerEmailCapture', 'customerEmailCapture'),
  propOnly('behavior.localMessageFlows', 'localMessageFlows'),

  // features
  dup('features.allowAttachments', 'allowAttachments', 'features', true),
  dup('features.allowVoice', 'allowVoice', 'features', false),
  dup('features.allowVideo', 'allowVideo', 'features', false),
  dup('features.enableStreaming', 'enableStreaming', 'features', false),
  dup('features.enableToolVisualization', 'enableToolVisualization', 'features', true),
  dup('features.enableRichMessages', 'enableRichMessages', 'features', true),
  dup('features.enableSourceCitations', 'enableSourceCitations', 'features', false),
  dup('features.enableThinking', 'enableThinking', 'features', true),

  // sessions
  {
    key: 'sessions.enabled',
    props: ['enableSessions'],
    settings: ['sessionHistory.enabled', 'enableSessions'],
    fallback: false,
    dup: true,
  },
  dup('sessions.maxSessions', 'maxSessions', 'sessionHistory', 50),
  {
    key: 'sessions.persistThread',
    props: ['persistThread'],
    settings: ['session.persistThread', 'persistThread'],
    fallback: true,
    dup: true,
  },
  {
    key: 'sessions.config',
    props: ['session'],
    settings: ['session'],
    fallback: EMPTY_OBJECT,
    merge: true,
    dup: true,
  },
  {
    key: 'sessions.variables',
    props: ['sessionVariables'],
    settings: ['sessionVariables'],
    dup: true,
  },
  {
    key: 'sessions.idleTimeout',
    props: ['idleTimeout'],
    settings: ['idleTimeout'],
    fallback: EMPTY_OBJECT,
    merge: true,
    dup: true,
  },
  {
    key: 'sessions.customerId',
    props: ['customerId'],
    settings: ['customerId'],
    fallback: '',
    dup: true,
  },

  // analytics
  {
    key: 'analytics.enabled',
    props: ['analyticsEnabled'],
    settings: ['analytics.enabled', 'analyticsEnabled'],
    dup: true,
  },
  {
    key: 'analytics.baseUrl',
    props: ['analyticsBaseUrl'],
    settings: ['endpoints.analyticsBaseUrl', 'analyticsBaseUrl'],
    fallback: null,
    dup: true,
  },
  {
    key: 'analytics.endpoint',
    props: ['analyticsEndpoint'],
    settings: ['endpoints.analyticsEndpoint', 'analyticsEndpoint'],
    fallback: null,
    dup: true,
  },
  {
    key: 'analytics.sampleRate',
    props: ['analyticsSampleRate'],
    settings: ['analytics.sampleRate', 'analyticsSampleRate'],
    dup: true,
  },
  {
    key: 'analytics.apiKey',
    props: ['analyticsApiKey'],
    settings: ['analytics.apiKey', 'analyticsApiKey'],
    fallback: null,
    dup: true,
  },
  { key: 'analytics.outcomes', props: ['outcomes'], settings: ['outcomes'], dup: true },

  // telemetry
  {
    key: 'telemetry.enabled',
    props: ['telemetryEnabled'],
    settings: ['debug.telemetry.enabled', 'telemetry.enabled', 'telemetryEnabled'],
    fallback: false,
    dup: true,
  },
  {
    key: 'telemetry.endpoint',
    props: ['telemetryEndpoint'],
    settings: ['debug.telemetry.endpoint', 'telemetry.endpoint', 'telemetryEndpoint'],
    fallback: null,
    dup: true,
  },
  {
    key: 'telemetry.pollIntervalMs',
    props: ['telemetryPollIntervalMs'],
    settings: [
      'debug.telemetry.pollIntervalMs',
      'telemetry.pollIntervalMs',
      'telemetryPollIntervalMs',
    ],
    fallback: 5000,
    dup: true,
  },
  {
    key: 'telemetry.showContext',
    props: ['telemetryShowContext'],
    settings: ['debug.telemetry.showContext', 'telemetry.showContext', 'telemetryShowContext'],
    fallback: true,
    dup: true,
  },

  // security
  {
    key: 'security.apiKey',
    props: ['apiKey'],
    settings: ['apiKey', 'auth.apiKey'],
    fallback: null,
  },
  propOnly('security.settingsApiKey', 'settingsApiKey', null),
  dup('security.widgetAuthToken', 'widgetAuthToken', 'security', ''),
  dup('security.allowedDomains', 'allowedDomains', 'security', EMPTY_ARRAY),
  dup('security.allowedOrigins', 'allowedOrigins', 'security', EMPTY_ARRAY),
  dup('security.cspNonce', 'cspNonce', 'security', ''),
  dup('security.customCss', 'customCss', 'security', ''),
  {
    key: 'security.consent',
    props: ['consent'],
    settings: ['security.consent', 'consent'],
    fallback: EMPTY_OBJECT,
    merge: true,
    dup: true,
  },
  { key: 'security.consentRequired', settings: ['security.consentRequired'] },
  dup('security.doNotTrain', 'doNotTrain', 'security'),
  dup('security.retentionDays', 'retentionDays', 'security'),

  // limits
  dup('limits.maxInputLength', 'maxInputLength', 'limits'),
  dup('limits.inputWarningThreshold', 'inputWarningThreshold', 'limits'),
  dup('limits.maxMessagesPerSession', 'maxMessagesPerSession', 'limits'),
  dup('limits.rateLimit', 'rateLimit', 'limits'),
  dup('limits.maxFileSizeMB', 'maxFileSizeMB', 'limits'),
  dup('limits.maxFileCount', 'maxFileCount', 'limits'),
  dup('limits.allowedFileTypes', 'allowedFileTypes', 'limits'),

  // commerce
  dup('commerce.cartMode', 'cartMode', 'commerce', 'auto'),

  // callbacks
  ...CALLBACK_PROPS.map(name => propOnly(`callbacks.${name}`, name)),
];

/**
 * Inputs that are accepted for backwards compatibility but no longer feed the
 * canonical config. `handoff` has no consumer: live-agent handoff is driven by
 * business actions and `onHandoffRequest`.
 */
export const IGNORED_INPUTS: ReadonlyArray<{ source: 'prop' | 'settings'; path: string }> = [
  { source: 'prop', path: 'handoff' },
  { source: 'settings', path: 'handoff' },
];

function flatten(): ConfigAlias[] {
  const rows: ConfigAlias[] = [];
  for (const spec of CONFIG_KEYS) {
    const canonicalSetting = spec.settings?.[0];
    const replacement = canonicalSetting ? `settings.${canonicalSetting}` : undefined;
    for (const path of spec.props || []) {
      rows.push({
        source: 'prop',
        path,
        canonical: spec.key,
        deprecated: !!spec.dup,
        ...(spec.dup && replacement ? { replacement } : {}),
      });
    }
    (spec.settings || []).forEach((path, index) => {
      rows.push({
        source: 'settings',
        path,
        canonical: spec.key,
        deprecated: index > 0,
        ...(index > 0 && replacement ? { replacement } : {}),
      });
    });
  }
  for (const ignored of IGNORED_INPUTS) {
    rows.push({ ...ignored, canonical: null, deprecated: true });
  }
  return rows;
}

/** One row per accepted prop / settings path. */
export const CONFIG_ALIASES: ReadonlyArray<ConfigAlias> = /*#__PURE__*/ flatten();

/** Top-level `ChatWidgetProps` keys the widget understands (including the `settings` container). */
export const KNOWN_PROP_KEYS: ReadonlySet<string> = /*#__PURE__*/ knownPropKeys();

/** Every accepted `settings` dot path. */
export const KNOWN_SETTINGS_PATHS: ReadonlySet<string> = /*#__PURE__*/ knownSettingsPaths();

// The derived tables above are only read by dev-time validation, tests and the docs
// scripts. Building them in functions called through `#__PURE__` lets the production
// build drop them (and `flatten()`) entirely; the values are unchanged.
function knownPropKeys(): ReadonlySet<string> {
  return new Set([
    'settings',
    ...CONFIG_ALIASES.filter(a => a.source === 'prop').map(a => a.path.split('.')[0]),
  ]);
}

function knownSettingsPaths(): ReadonlySet<string> {
  return new Set(CONFIG_ALIASES.filter(a => a.source === 'settings').map(a => a.path));
}
